import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { NAV_SECTIONS, SectionId } from "../../App";

interface SidebarProps {
  activeSection: SectionId;
  onNavigate:    (id: SectionId) => void;
}

function groupSections(sections: typeof NAV_SECTIONS) {
  const groups: Record<string, typeof NAV_SECTIONS> = {};
  sections.forEach(s => {
    if (!groups[s.group]) groups[s.group] = [];
    groups[s.group].push(s);
  });
  return groups;
}

export default function Sidebar({ activeSection, onNavigate }: SidebarProps) {
  const { theme, toggleTheme } = useTheme();
  const groups = groupSections(NAV_SECTIONS);

  return (
    <div style={{
      width:           "var(--sidebar-width)",
      minWidth:        "var(--sidebar-width)",
      backgroundColor: "var(--bg-sidebar)",
      borderRight:     "1px solid var(--border-sidebar)",
      display:         "flex",
      flexDirection:   "column",
      overflow:        "hidden",
      flexShrink:      0,
    }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
        {Object.entries(groups).map(([groupName, sections]) => (
          <div key={groupName} style={{ marginBottom: "4px" }}>
            <div style={{
              fontSize:      "9px",
              fontWeight:    600,
              color:         "#334155",
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              padding:       "8px 16px 4px",
            }}>
              {groupName}
            </div>
            {sections.map(section => {
              const isActive   = activeSection === section.id;
              const isDisabled = !section.ready;
              return (
                <div
                  key={section.id}
                  title={isDisabled ? "Coming soon" : section.label}
                  onClick={() => { if (!isDisabled) onNavigate(section.id); }}
                  style={{
                    display:         "flex",
                    alignItems:      "center",
                    gap:             "8px",
                    padding:         "7px 16px",
                    margin:          "1px 8px",
                    borderRadius:    "6px",
                    cursor:          isDisabled ? "default" : "pointer",
                    backgroundColor: isActive ? "#1E3A5F" : "transparent",
                    borderLeft:      isActive ? "2px solid var(--accent-gold)" : "2px solid transparent",
                    transition:      "background-color 0.15s ease",
                  }}
                  onMouseEnter={e => {
                    if (!isDisabled && !isActive)
                      (e.currentTarget as HTMLDivElement).style.backgroundColor = "#152A45";
                  }}
                  onMouseLeave={e => {
                    if (!isActive)
                      (e.currentTarget as HTMLDivElement).style.backgroundColor = "transparent";
                  }}
                >
                  <div style={{
                    width:           "5px",
                    height:          "5px",
                    borderRadius:    "50%",
                    backgroundColor: isActive ? "var(--accent-gold)" : isDisabled ? "#1E3A5F" : "#2D4A6E",
                    flexShrink:      0,
                  }} />
                  <span style={{
                    fontSize:   "12px",
                    fontWeight: isActive ? 600 : 400,
                    color:      isActive ? "#FFFFFF" : isDisabled ? "#334155" : "var(--text-sidebar)",
                  }}>
                    {section.label}
                  </span>
                  {isDisabled && (
                    <span style={{
                      marginLeft:      "auto",
                      fontSize:        "9px",
                      color:           "#334155",
                      backgroundColor: "#0F1C2E",
                      padding:         "1px 5px",
                      borderRadius:    "3px",
                      fontWeight:      500,
                    }}>
                      soon
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ borderTop: "1px solid var(--border-sidebar)", padding: "12px 16px", flexShrink: 0 }}>
        <button
          onClick={toggleTheme}
          style={{
            display:         "flex",
            alignItems:      "center",
            gap:             "8px",
            width:           "100%",
            padding:         "7px 10px",
            borderRadius:    "6px",
            border:          "1px solid #1E3A5F",
            backgroundColor: "transparent",
            cursor:          "pointer",
            transition:      "background-color 0.15s ease",
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#152A45"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"; }}
        >
          <span style={{ fontSize: "14px" }}>{theme === "light" ? "🌙" : "☀️"}</span>
          <span style={{ fontSize: "11px", color: "#64748B", fontWeight: 500 }}>
            {theme === "light" ? "Dark mode" : "Light mode"}
          </span>
        </button>
      </div>
    </div>
  );
}
